import * as API from '../../lib/api'

const SCHEMA_VERSION = 'v1'
const KEY_PREFIX = `crashdown:checkout_draft:${SCHEMA_VERSION}`

export interface ICheckoutDraft {
  customer: Pick<API.ICustomer, 'email' | 'first_name' | 'last_name' | 'company'>
  billingAddress: Pick<API.IAddress, 'address1' | 'address2' | 'city' | 'state' | 'province' | 'zip' | 'country'>
  selectedPurchaseOptionName: API.PurchaseOptionNameType
  termsAccepted: boolean
  currentStepId: string
}

// `plan_code` and `trial` live in the checkout URL's query string, so they
// survive a reload without being stored. Keying on them keeps a draft from
// leaking across plans.
export function draftStorageKey(planCode: string, trial: boolean): string {
  return `${KEY_PREFIX}:${planCode}:${trial}`
}

// `state.checkout` is a `Checkout` (../../lib/checkout.ts) — a class holding
// `Date` objects, so it cannot be JSON round-tripped wholesale.
interface IDraftSource {
  checkout: { cashier: Pick<API.ICashier, 'customer' | 'billing_address'> }
  selectedPurchaseOptionName: API.PurchaseOptionNameType
  termsAccepted: boolean
  currentStepId: string
}

export function buildDraft(state: IDraftSource): ICheckoutDraft {
  const { customer, billing_address: address } = state.checkout.cashier

  return {
    customer: {
      email: customer.email,
      first_name: customer.first_name,
      last_name: customer.last_name,
      company: customer.company,
    },
    billingAddress: {
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      state: address.state,
      province: address.province,
      zip: address.zip,
      country: address.country,
    },
    selectedPurchaseOptionName: state.selectedPurchaseOptionName,
    termsAccepted: state.termsAccepted,
    currentStepId: state.currentStepId,
  }
}

function isPlainObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// A whole draft is dropped rather than half-hydrated, matching the version-stamp
// behavior: a field group that is present but not a plain object would reach
// `$merge` and throw out of the consuming constructor.
function isRestorableDraft(value: unknown): boolean {
  if (!isPlainObject(value)) {
    return false
  }
  const { customer, billingAddress } = value as Partial<ICheckoutDraft>

  return (
    (customer === undefined || isPlainObject(customer)) &&
    (billingAddress === undefined || isPlainObject(billingAddress))
  )
}

export function readDraft(key: string): ICheckoutDraft | null {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)
    return isRestorableDraft(parsed) ? (parsed as ICheckoutDraft) : null
  } catch (e) {
    return null
  }
}

export function writeDraft(key: string, draft: ICheckoutDraft): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(draft))
  } catch (e) {
    return
  }
}

export function clearDraft(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch (e) {
    return
  }
}
